import { capitalizeFirstLetter, cloneNested, transformDateToHumanReadablePretty } from '@/helpers/helpers'
import { computed, ref } from '@vue/composition-api'
import dayjs from 'dayjs'
import useAppConfig from '@core/app-config/useAppConfig'
import store from '@/store/store'
import { $themeBreakpoints } from '@themeConfig'

export default () => {
  const { skin } = useAppConfig()
  const isDark = computed(() => skin.value === 'dark')
  const refreshKey = ref(0)
  const weeks = ref({})
  const daysByDate = ref({})
  const pendingUpdateTimeouts = {}
  const rollbackSnapshots = {}

  const userRolesForScheduling = computed(() => store.getters['appConfig/config'].schedule.userRolesForScheduling)
  const shiftDefinitions = computed(() => store.getters['appConfig/config'].schedule.shiftDefinitions)

  const currentMonth = ref(dayjs().startOf('month').add(1, 'month'))
  const periodStart = computed(() => currentMonth.value.startOf('week'))
  const periodEnd = computed(() => currentMonth.value.add(1, 'month').endOf('week'))

  const teamDefinitions = computed(() => store.getters['settings/getTeamDefinitions'])
  const teamDefinitionsAsCheckboxOptions = computed(() => teamDefinitions.value.map(teamDefinition => ({ text: teamDefinition.code, value: teamDefinition.id })))

  const isMobileWidth = computed(() => $themeBreakpoints.lg > store.state.app.windowWidth)

  const dayjsWeekdays = computed(() => {
    const weekdays = cloneNested(dayjs.localeData().weekdays())
    if (dayjs.localeData().firstDayOfWeek() === 1) {
      weekdays.push(weekdays.shift())
    }
    return weekdays
  })
  const loading = ref(false)

  const defaultQuantityModel = {}
  shiftDefinitions.value.forEach(shiftDefinition => {
    defaultQuantityModel[shiftDefinition.id] = { roles: [], teams: [] }
    userRolesForScheduling.value.forEach(role => {
      defaultQuantityModel[shiftDefinition.id].roles.push({
        role,
        quantity: 0,
      })
    })
  })

  const updateHeadcountsByStaffingNeeds = (staffingNeeds, shiftDefinitionIdToUpdate = null, modifier = null) => {
    const updatedDates = []
    staffingNeeds.forEach(staffingNeed => {
      const { shiftDefinitionId } = staffingNeed
      if (shiftDefinitionIdToUpdate && shiftDefinitionIdToUpdate !== shiftDefinitionId) {
        return
      }
      let date = dayjs(staffingNeed.date)
      if (modifier) {
        date = date.add(modifier, 'day')
      }
      const dateFormatted = date.format('YYYY-MM-DD')
      const day = daysByDate.value[dateFormatted]
      if (!day || !day.isCurrentMonth) {
        return
      }

      const shiftHeadcount = day.headcounts[shiftDefinitionId]
      if (!shiftHeadcount) {
        return
      }

      const roleIndex = shiftHeadcount.roles.findIndex(role => role.role === staffingNeed.role)
      if (roleIndex !== -1) {
        shiftHeadcount.roles[roleIndex].quantity = staffingNeed.quantity
      }

      staffingNeed.teamDefinitions.forEach(teamDefinition => {
        if (!shiftHeadcount.teams.includes(teamDefinition.id)) {
          shiftHeadcount.teams.push(teamDefinition.id)
        }
      })

      if (!updatedDates.includes(dateFormatted)) {
        updatedDates.push(dateFormatted)
      }
    })

    return updatedDates
  }

  const fetchStaffingNeeds = () => {
    loading.value = true
    store.dispatch(
      'schedules/fetchStaffingNeeds',
      {
        dateFrom: currentMonth.value.startOf('month').format('YYYY-MM-DD'),
        dateTo: currentMonth.value.endOf('month').format('YYYY-MM-DD'),
      },
    )
      .then(response => {
        updateHeadcountsByStaffingNeeds(response.data)
      })
      .catch(err => {
        window.toast.notify.danger(err.message)
        throw err
      })
      .finally(() => {
        loading.value = false
      })
  }

  const init = () => {
    loading.value = true
    daysByDate.value = {}
    let date = periodStart.value.clone()
    let week = {}
    const month = []

    while (date < periodEnd.value) {
      week = {
        number: date.week(),
        days: [],
      }
      for (let i = 0; i < 7; i += 1) {
        const dayState = {
          date,
          weekday: date.weekday(),
          formatted: date.format('YYYY-MM-DD'),
          isToday: date.isToday(),
          isCurrentMonth: currentMonth.value.format('YYYYMM') === date.format('YYYYMM'),
          isPast: date.isBefore(dayjs(), 'day'),
          headcounts: cloneNested(defaultQuantityModel),
        }
        week.days.push(dayState)
        daysByDate.value[dayState.formatted] = dayState

        date = date.add(1, 'day')
      }
      month.push(week)
      week = []
    }

    weeks.value = month
    fetchStaffingNeeds()
  }

  store.dispatch('settings/fetchTeamDefinitions', { sortField: 'code', sortOrder: 'asc' })
    .then(() => {
      init()
    })

  const nextMonth = () => {
    currentMonth.value = currentMonth.value.add(1, 'month')
    init()
  }

  const prevMonth = () => {
    currentMonth.value = currentMonth.value.subtract(1, 'month')
    init()
  }

  const update = (day, headcounts, timeout = 1000) => {
    const date = day.format('YYYY-MM-DD')
    if (pendingUpdateTimeouts[date]) {
      clearTimeout(pendingUpdateTimeouts[date])
    }
    if (!rollbackSnapshots[date]) {
      rollbackSnapshots[date] = cloneNested(daysByDate.value[date]?.headcounts || {})
    }

    pendingUpdateTimeouts[date] = setTimeout(() => {
      store.dispatch('schedules/updateStaffingNeeds', { date, headcounts })
        .then(() => {
          delete rollbackSnapshots[date]
        })
        .catch(err => {
          window.toast.notify.danger(err.message)
          if (rollbackSnapshots[date] && daysByDate.value[date]) {
            daysByDate.value[date].headcounts = rollbackSnapshots[date]
          }
          delete rollbackSnapshots[date]
          throw err
        })
    }, timeout)
  }

  const resetHeadcounts = (shiftDefinitionId, modifier, dayFromParam, dayToParam) => {
    const dayFrom = dayFromParam.add(modifier, 'day')
    const dayTo = dayToParam.add(modifier, 'day')

    weeks.value.forEach((week, weekKey) => {
      week.days.forEach((day, dayKey) => {
        if (!day.isCurrentMonth) {
          return
        }
        if (day.date.isBefore(dayFrom) || day.date.isAfter(dayTo)) {
          return
        }

        day.headcounts[shiftDefinitionId].roles.forEach((role, roleKey) => {
          weeks.value[weekKey].days[dayKey].headcounts[shiftDefinitionId].roles[roleKey].quantity = 0
        })
        weeks.value[weekKey].days[dayKey].headcounts[shiftDefinitionId].teams = []
      })
    })
  }

  const copyFromDay = (shiftDefinitionId, modifier, dayFrom, dayToParam = null) => {
    let dayTo = dayToParam
    if (!dayTo) {
      dayTo = dayFrom
    }

    resetHeadcounts(shiftDefinitionId, modifier, dayFrom, dayTo)

    store.dispatch(
      'schedules/fetchStaffingNeeds',
      {
        dateFrom: dayFrom.format('YYYY-MM-DD'),
        dateTo: dayTo.format('YYYY-MM-DD'),
      },
    )
      .then(response => {
        const updatedDates = updateHeadcountsByStaffingNeeds(response.data, shiftDefinitionId, modifier)
        updatedDates.forEach(date => {
          const day = daysByDate.value[date]
          if (!day) {
            return
          }
          update(
            dayjs(date),
            day.headcounts,
            0,
          )
        })
      })
  }

  return {
    isDark,
    shiftDefinitions,

    refreshKey,
    currentMonth,
    dayjsWeekdays,
    weeks,
    isMobileWidth,
    loading,

    update,

    nextMonth,
    prevMonth,
    copyFromDay,

    teamDefinitionsAsCheckboxOptions,

    dayjs,
    capitalizeFirstLetter,
    transformDateToHumanReadablePretty,
  }
}
